/**
 * Combined cross-group prayer feeds ("All Prayers" / "Lifted" views).
 *
 * No cross-group backend endpoint exists, so this fans out one GET per group
 * the user belongs to — across ALL orgs — and merges client-side. Cross-org
 * requests pin their org via the `organizationId` query param, which
 * `setOrganizationContextSecure` resolves ahead of the X-Organization-Id
 * header the axios interceptor always sends (membership is verified
 * server-side per request, privacy filtered per viewer role).
 *
 * Cache shape per group is `{ prayers: [...] }` so usePrayerActions can
 * optimistically patch the ['combined-prayers'] family alongside ['prayers'].
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import type { Prayer } from '@flock-types/prayer.types';
import { isPrayerLifted } from '@flock-types/prayer.types';
import { sortPrayersLiftedFirst, transformPrayer } from '@/hooks/usePrayers';

const PAGE_LIMIT = 50;

export interface CombinedPrayer extends Prayer {
  groupName: string;
  organizationName: string;
}

interface GroupTuple {
  groupId: string;
  groupName: string;
  orgId: string;
  orgName: string;
}

export interface UseCombinedPrayersResult {
  prayers: CombinedPrayer[];
  loading: boolean;
  /** Groups whose fetch failed — the rest still render. */
  errors: Array<{ groupId: string; groupName: string; message: string }>;
  partialFailure: boolean;
  /** Groups with more prayers than the first page shown here. */
  groupsWithMore: Array<{ groupId: string; groupName: string }>;
  refetch: () => void;
}

export function useCombinedPrayers(
  mode: 'all' | 'lifted',
  status: 'active' | 'answered' | 'all' = 'active'
): UseCombinedPrayersResult {
  const { organizations, isAuthenticated, isSwitchingOrg } = useAuth();
  const { isOrgLocked } = useOrgLockState();

  const groups = useMemo((): GroupTuple[] => {
    const tuples: GroupTuple[] = [];
    for (const membership of organizations) {
      // Paused org: omit its slice — prayer reads 403 while locked.
      if (isOrgLocked(membership.organization_id)) continue;
      for (const group of membership.groups ?? []) {
        tuples.push({
          groupId: group.id,
          groupName: group.name,
          orgId: membership.organization_id,
          orgName: membership.organization.name,
        });
      }
    }
    return tuples;
  }, [organizations, isOrgLocked]);

  const queries = useQueries({
    queries: groups.map((g) => ({
      queryKey: ['combined-prayers', g.groupId, status],
      queryFn: async () => {
        const params = new URLSearchParams();
        params.set('limit', String(PAGE_LIMIT));
        if (status !== 'all') params.set('status', status);
        // Pin the request to THIS group's org (cross-org mechanism)
        params.set('organizationId', g.orgId);
        const response = await api.get<any>(
          `/prayers/groups/${g.groupId}/prayers?${params.toString()}`
        );
        const prayerData: any[] = response?.data?.prayers || response?.prayers || [];
        return {
          prayers: prayerData.map((p) => transformPrayer(p, g.orgId, g.groupId)),
          hasMore: response?.data?.hasMore ?? prayerData.length === PAGE_LIMIT,
        };
      },
      enabled: isAuthenticated && !isSwitchingOrg,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    })),
  });

  return useMemo(() => {
    const merged: CombinedPrayer[] = [];
    const errors: UseCombinedPrayersResult['errors'] = [];
    const groupsWithMore: UseCombinedPrayersResult['groupsWithMore'] = [];

    queries.forEach((q, i) => {
      const g = groups[i];
      if (q.error) {
        errors.push({
          groupId: g.groupId,
          groupName: g.groupName,
          message: (q.error as Error).message,
        });
        return;
      }
      if (!q.data) return;
      if (q.data.hasMore) groupsWithMore.push({ groupId: g.groupId, groupName: g.groupName });
      for (const p of q.data.prayers) {
        merged.push({ ...p, groupName: g.groupName, organizationName: g.orgName });
      }
    });

    const filtered = mode === 'lifted' ? merged.filter((p) => isPrayerLifted(p)) : merged;

    return {
      prayers: sortPrayersLiftedFirst(filtered),
      loading: groups.length > 0 && queries.some((q) => q.isLoading),
      errors,
      partialFailure: errors.length > 0 && errors.length < groups.length,
      groupsWithMore,
      refetch: () => queries.forEach((q) => void q.refetch()),
    };
  }, [queries, groups, mode]);
}
